import React, { useEffect, useState } from 'react';
import client from '../api/client';

const PRIMARY = '#B71530';

const FALLBACK_FEATURES = [
  { key: 'branding',            label: 'Branding',            description: 'Allows the org to customise logo, favicon, display name, and primary colour.' },
  { key: 'data_imports',        label: 'Data Imports',        description: 'Access to the Data Imports tab in Settings.' },
  { key: 'azure_config',        label: 'Azure Configuration', description: 'Access to Azure / Document Intelligence credentials.' },
  { key: 'matching_tolerances', label: 'Matching Tolerances', description: 'Adjust fuzzy-match and amount-tolerance thresholds.' },
  { key: 'global_settings',     label: 'Global Settings',      description: 'Organisation admin can export all data as JSON/CSV and permanently delete the organisation.' },
];

const POLL_PRESETS = [
  { label: '5 minutes',   minutes: 5 },
  { label: '15 minutes',  minutes: 15 },
  { label: '30 minutes',  minutes: 30 },
  { label: '1 hour',      minutes: 60 },
  { label: '2 hours',     minutes: 120 },
  { label: '6 hours',     minutes: 360 },
  { label: '12 hours',    minutes: 720 },
];

function formatMinutes(m) {
  if (m < 60) return `${m} minute${m === 1 ? '' : 's'}`;
  const h = m / 60;
  return `${h} hour${h === 1 ? '' : 's'}`;
}

// ── Version Poll Interval card ────────────────────────────────────────────────

function PollIntervalCard() {
  const [intervalMinutes, setIntervalMinutes] = useState(360);
  const [savedMinutes, setSavedMinutes]       = useState(null);
  const [saving, setSaving]   = useState(false);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg]         = useState({ text: '', type: 'success' });

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'success' }), 4000);
  };

  useEffect(() => {
    client.get('/security/poll-config')
      .then(r => {
        const m = r.data.interval_minutes || 360;
        setIntervalMinutes(m);
        setSavedMinutes(m);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const isPreset = POLL_PRESETS.some(p => p.minutes === intervalMinutes);
  const isDirty  = intervalMinutes !== savedMinutes;

  const handleSave = async () => {
    setSaving(true);
    try {
      await client.patch('/security/poll-config', { interval_minutes: intervalMinutes });
      setSavedMinutes(intervalMinutes);
      showMsg(`Poll interval set to ${formatMinutes(intervalMinutes)}. New tabs will pick this up automatically.`);
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Failed to save', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="card mt-4">
      <div className="card-header fw-semibold" style={{ fontSize: '14px', backgroundColor: '#f8f9fa' }}>
        Version Check Interval
      </div>
      <div className="card-body">
        {msg.text && (
          <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '13px' }}>
            {msg.text}
          </div>
        )}
        <p style={{ fontSize: '13px', color: '#495057', marginBottom: '16px' }}>
          How often open browser tabs poll <code>/api/security/version</code> to detect a new
          deployment. Shorter intervals mean faster notifications; longer intervals reduce
          server load. The default is <strong>6 hours</strong>.
        </p>

        {loading ? (
          <div className="text-center py-2"><div className="spinner-border spinner-border-sm text-secondary" /></div>
        ) : (
          <>
            {/* Preset buttons */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '16px' }}>
              {POLL_PRESETS.map(p => (
                <button
                  key={p.minutes}
                  type="button"
                  onClick={() => setIntervalMinutes(p.minutes)}
                  className={`btn btn-sm ${intervalMinutes === p.minutes ? 'btn-primary' : 'btn-outline-secondary'}`}
                  style={intervalMinutes === p.minutes ? { backgroundColor: PRIMARY, borderColor: PRIMARY } : {}}
                >
                  {p.label}
                </button>
              ))}
            </div>

            {/* Custom numeric input */}
            <div className="d-flex align-items-center" style={{ gap: '10px', marginBottom: '4px' }}>
              <label style={{ fontSize: '13px', fontWeight: 600, margin: 0, whiteSpace: 'nowrap' }}>
                Custom (minutes):
              </label>
              <input
                type="number"
                min={5}
                max={720}
                value={intervalMinutes}
                onChange={e => setIntervalMinutes(Math.max(5, Math.min(720, parseInt(e.target.value) || 5)))}
                className="form-control form-control-sm"
                style={{ width: '90px' }}
              />
              <span style={{ fontSize: '12px', color: '#6c757d' }}>
                = {formatMinutes(intervalMinutes)}
                {!isPreset && <span className="badge badge-secondary ml-2" style={{ fontSize: '10px' }}>custom</span>}
              </span>
            </div>
            <div style={{ fontSize: '11px', color: '#6c757d', marginBottom: '16px' }}>
              Range: 5 minutes – 12 hours
            </div>

            {/* Slider */}
            <input
              type="range"
              min={5}
              max={720}
              step={5}
              value={intervalMinutes}
              onChange={e => setIntervalMinutes(parseInt(e.target.value))}
              className="custom-range"
              style={{ width: '100%', marginBottom: '16px', accentColor: PRIMARY }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: '11px', color: '#adb5bd', marginTop: '-10px', marginBottom: '16px' }}>
              <span>5 min</span>
              <span>1 h</span>
              <span>3 h</span>
              <span>6 h</span>
              <span>12 h</span>
            </div>

            <button
              className="btn btn-sm"
              style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none', opacity: isDirty ? 1 : 0.6 }}
              onClick={handleSave}
              disabled={saving || !isDirty}
            >
              {saving ? 'Saving…' : isDirty ? `Save — ${formatMinutes(intervalMinutes)}` : 'Saved'}
            </button>

            {savedMinutes !== null && (
              <span style={{ fontSize: '12px', color: '#6c757d', marginLeft: '12px' }}>
                Current: <strong>{formatMinutes(savedMinutes)}</strong>
              </span>
            )}
          </>
        )}
      </div>
    </div>
  );
}


// ── Feature Flags tab ─────────────────────────────────────────────────────────

function FeatureFlagsTab() {
  const [orgs, setOrgs] = useState([]);
  const [selected, setSelected] = useState(null);
  const [features, setFeatures] = useState({});
  const [catalogue, setCatalogue] = useState([]);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState({ text: '', type: 'success' });
  const [loadingFeatures, setLoadingFeatures] = useState(false);

  const showMsg = (text, type = 'success') => {
    setMsg({ text, type });
    setTimeout(() => setMsg({ text: '', type: 'success' }), 4000);
  };

  useEffect(() => {
    client.get('/orgs/features/catalogue').then(r => {
      const raw = r.data.features || [];
      setCatalogue(raw.map(f => (typeof f === 'string' ? { key: f, label: f, description: '' } : f)));
    }).catch(() => {});
    client.get('/orgs/').then(r => setOrgs(r.data.organizations || [])).catch(() => {});
  }, []);

  const selectOrg = (org) => {
    setSelected(org);
    setFeatures({});
    setLoadingFeatures(true);
    client.get(`/orgs/${org.id}/features`)
      .then(r => setFeatures(r.data.features || {}))
      .catch(() => {})
      .finally(() => setLoadingFeatures(false));
  };

  const toggle = (key) => setFeatures(prev => ({ ...prev, [key]: !prev[key] }));

  const handleSave = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await client.patch(`/orgs/${selected.id}/features`, { features });
      showMsg(`Features updated for ${selected.display_name}`);
    } catch (err) {
      showMsg(err?.response?.data?.detail || 'Failed to save', 'danger');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '20px' }}>
        Control which Settings tabs are visible to each organisation. By default all features
        are enabled. Untick to hide a tab from that organisation's Settings page.
      </p>

      {msg.text && (
        <div className={`alert alert-${msg.type} py-2 mb-3`} style={{ fontSize: '13px' }}>
          {msg.text}
        </div>
      )}

      <div className="row" style={{ margin: 0 }}>
        {/* Org list */}
        <div className="col-md-4" style={{ paddingLeft: 0, paddingRight: '16px' }}>
          <div className="card">
            <div className="card-header fw-semibold" style={{ fontSize: '13px', backgroundColor: '#f8f9fa' }}>
              Organisations
            </div>
            <div className="list-group list-group-flush">
              {orgs.length === 0 && (
                <div className="list-group-item text-muted" style={{ fontSize: '13px' }}>
                  No organisations found
                </div>
              )}
              {orgs.map(org => (
                <button
                  key={org.id}
                  className="list-group-item list-group-item-action"
                  style={{
                    fontSize: '13px',
                    backgroundColor: selected?.id === org.id ? '#fff5f5' : undefined,
                    borderLeft: selected?.id === org.id ? `3px solid ${PRIMARY}` : '3px solid transparent',
                    fontWeight: selected?.id === org.id ? 600 : 400,
                  }}
                  onClick={() => selectOrg(org)}
                >
                  <div>{org.display_name || org.subdomain}</div>
                  <div style={{ fontSize: '11px', color: '#6c757d' }}>{org.subdomain}</div>
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Feature toggles */}
        <div className="col-md-8" style={{ paddingLeft: 0, paddingRight: 0 }}>
          {!selected ? (
            <div
              className="card"
              style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '200px' }}
            >
              <div className="text-muted" style={{ fontSize: '13px' }}>
                Select an organisation to configure its features
              </div>
            </div>
          ) : (
            <div className="card">
              <div
                className="card-header d-flex align-items-center justify-content-between"
                style={{ fontSize: '13px', backgroundColor: '#f8f9fa' }}
              >
                <span className="fw-semibold">{selected.display_name || selected.subdomain}</span>
                <span className="badge badge-secondary" style={{ fontSize: '11px' }}>{selected.subdomain}</span>
              </div>
              <div className="card-body">
                {loadingFeatures ? (
                  <div className="text-center py-4">
                    <div className="spinner-border spinner-border-sm text-danger" />
                  </div>
                ) : (
                  <>
                    <p style={{ fontSize: '12px', color: '#6c757d', marginBottom: '16px' }}>
                      Settings tabs visible to users in this organisation:
                    </p>
                    {(catalogue.length > 0 ? catalogue : FALLBACK_FEATURES).map(feat => {
                      const key = feat.key;
                      const enabled = features[key] !== false;
                      return (
                        <div
                          key={key}
                          className="d-flex align-items-start"
                          style={{ padding: '12px 0', borderBottom: '1px solid #f0f0f0', gap: '12px' }}
                        >
                          <div style={{ paddingTop: '2px' }}>
                            <input
                              type="checkbox"
                              id={`feat-${key}`}
                              checked={enabled}
                              onChange={() => toggle(key)}
                              style={{ width: '16px', height: '16px', cursor: 'pointer' }}
                            />
                          </div>
                          <label htmlFor={`feat-${key}`} style={{ cursor: 'pointer', margin: 0, flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 600, color: '#212529' }}>{feat.label}</div>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '2px' }}>{feat.description || ''}</div>
                          </label>
                          <span
                            className={`badge ${enabled ? 'badge-success' : 'badge-secondary'}`}
                            style={{ fontSize: '10px', alignSelf: 'center' }}
                          >
                            {enabled ? 'Enabled' : 'Disabled'}
                          </span>
                        </div>
                      );
                    })}
                    <div className="mt-4">
                      <button
                        className="btn btn-sm"
                        style={{ backgroundColor: PRIMARY, color: '#fff', border: 'none' }}
                        onClick={handleSave}
                        disabled={saving}
                      >
                        {saving ? 'Saving…' : 'Save Configuration'}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}


// ── Global Settings tab ───────────────────────────────────────────────────────

function GlobalSettingsTab() {
  return (
    <div>
      <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '0' }}>
        These settings apply across all organisations.
      </p>
      <PollIntervalCard />
    </div>
  );
}


// ── Page shell with tab navigation ───────────────────────────────────────────

const CONFIG_TABS = [
  { id: 'features', label: 'Feature Flags' },
  { id: 'global',   label: 'Global Settings' },
];

export default function AppAdminConfig() {
  const [activeTab, setActiveTab] = useState('features');

  return (
    <div>
      {/* Tab bar */}
      <ul className="nav app-tabs mb-4">
        {CONFIG_TABS.map(tab => (
          <li className="nav-item" key={tab.id}>
            <button
              className={`nav-link${activeTab === tab.id ? ' active' : ''}`}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          </li>
        ))}
      </ul>

      {activeTab === 'features' && <FeatureFlagsTab />}
      {activeTab === 'global'   && <GlobalSettingsTab />}
    </div>
  );
}
